import React from 'react';
import { Chip, Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { proDesignTokens } from './ProDesignTokens';
import { color, r } from '../../theme/tokens';

/**
 * Styled Pro Badge following Apple's minimalist design principles
 */
const StyledProBadge = styled(Chip)(({ theme, variant }) => {
  const baseStyles = {
    ...proDesignTokens.typography.proBadge,
    padding: proDesignTokens.components.proBadge.padding,
    background: proDesignTokens.components.proBadge.background,
    border: proDesignTokens.components.proBadge.border,
    borderRadius: proDesignTokens.components.proBadge.borderRadius,
    color: proDesignTokens.colors.neutral[600],
    boxShadow: proDesignTokens.shadows.proBadge,
    transition: proDesignTokens.transitions.medium,
    height: 'auto',
    '& .MuiChip-label': {
      padding: 0,
      ...proDesignTokens.typography.proBadge,
    },
    '&:hover': {
      background: proDesignTokens.colors.background.medium,
      boxShadow: proDesignTokens.shadows.subtle,
      transform: `translateY(-${r(0.5)})`,
    }
  };

  // Variant-specific styles
  if (variant === 'accent') {
    return {
      ...baseStyles,
      background: proDesignTokens.colors.pro.accentLight,
      border: `${r(1)} solid ${proDesignTokens.colors.pro.accent}`,
      // Evergreen, not accent: accent as a label colour measures 2.87–3.17:1
      // (STACK-506). Accent is a fill colour; evergreen is a text colour.
      color: color.evergreen,
      '&:hover': {
        background: proDesignTokens.colors.pro.accentMedium,
        boxShadow: `0 ${r(2)} ${r(4)} ${proDesignTokens.colors.pro.accentLight}`,
        transform: `translateY(-${r(0.5)})`,
      }
    };
  }

  if (variant === 'success') {
    return {
      ...baseStyles,
      background: alpha(color.accent, 0.1),
      border: `${r(1)} solid ${alpha(color.accent, 0.3)}`,
      color: color.evergreen,
      '&:hover': {
        background: alpha(color.accent, 0.15),
        boxShadow: `0 ${r(2)} ${r(4)} ${alpha(color.accent, 0.2)}`,
        transform: `translateY(-${r(0.5)})`,
      }
    };
  }

  return baseStyles;
});

/**
 * ProFeatureBadge - Apple-inspired minimalist Pro feature indicator
 * 
 * @param {Object} props
 * @param {string} [props.label='Pro'] - Badge label text
 * @param {string} [props.variant='default'] - Badge variant: 'default', 'accent', 'success'
 * @param {string} [props.size='small'] - Badge size: 'small', 'medium'
 * @param {Object} [props.sx] - Additional styling
 * @param {React.ReactNode} [props.icon] - Optional icon
 * @returns {React.ReactElement}
 */
function ProFeatureBadge({ 
  label = 'Pro',
  variant = 'default',
  size = 'small',
  sx = {},
  icon = null,
  ...props 
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        ...sx
      }}
    >
      <StyledProBadge
        label={label}
        variant={variant}
        size={size}
        icon={icon}
        {...props}
      />
    </Box>
  );
}

export default ProFeatureBadge; 